import { Box, Icon, InputLabel, OptionType, SelectMenu, TextInput } from '@fullstory/components';
import { intl } from '@fullstory/intl';
import { Space } from '@fullstory/proto/fs/services/dx-objects';
import { FilterOptionValueType } from '@fullstory/redux';
import React, { useMemo } from 'react';
import { defaultResourceOptions } from '../ResourceList';
import { CreateButton } from './CreateButton';
import { FormattedMessage } from 'react-intl';

const ALL_SPACES_OPTION: OptionType<null> = {
  key: 'all',
  value: null,
  label: intl.formatMessage({ defaultMessage: 'All spaces', id: 'C2k+jQ' }),
};

interface LibrarySearchBarProps<F extends FilterOptionValueType> {
  /**
   * Optionally override available filters.
   *
   * Note that the default selection, if a filter is not provided, will be the first option passed in here.
   *
   * @default all of them.
   */
  filterTypes?: Readonly<F[]>;
  filter: F;
  onFilterChange: (filter: F) => void;
  search: string;
  onSearchChange: (search: string) => void;
  /**
   * When supplied the Spaces filter is additionally shown.
   */
  spaces?: {
    /**
     * Spaces to show as options.
     */
    availableSpaces: Space[];
    /**
     * The active Space filter.
     */
    selectedSpace: Space | null;
    /**
     * A null space means no Space filter, or All Spaces.
     */
    onSelectedSpaceChange: (space: Space | null) => void;
  };
  /**
   * Padding supplied to the container Box.
   */
  padding?: React.ComponentProps<typeof Box>['padding'];
  selectMenuVariant?: React.ComponentProps<typeof SelectMenu>['variant'];
  selectMenuSize?: React.ComponentProps<typeof SelectMenu>['size'];
  children?: React.ReactNode;
  showLabels?: boolean;
}

export const LibrarySearchBarBase = <F extends FilterOptionValueType = FilterOptionValueType>({
  filterTypes,
  filter,
  onFilterChange,
  search,
  onSearchChange,
  spaces,
  padding,
  selectMenuVariant,
  selectMenuSize,
  showLabels,
  children,
}: LibrarySearchBarProps<F>) => {
  const filterOptions = useMemo(
    (): OptionType<F>[] =>
      // there is probably a better way to narrow this without the cast, but it should be safe.
      // bigger concern and TODO is if defaultResourceOptions differ from enumerated FilterOptionValueType's.
      // we should just bring these options in here.
      defaultResourceOptions.filter(
        o => !filterTypes || filterTypes.some(t => t === o.value),
      ) as OptionType<F>[],
    [filterTypes],
  );
  const selectedFilter = useMemo(
    () => filterOptions.find(o => o.value === filter),
    [filter, filterOptions],
  );

  const spacesOptions: OptionType<Space | null>[] = useMemo(() => {
    if (!spaces) return [];

    const options = spaces.availableSpaces.map(s => ({
      key: s.id,
      value: s,
      label: s.name,
    }));

    if (options.length === 1) return options;

    return [ALL_SPACES_OPTION, ...options];
  }, [spaces]);
  const selectedSpaceOption = useMemo(() => {
    if (!spaces?.selectedSpace) return ALL_SPACES_OPTION;
    return spacesOptions.find(o => o.value?.id === spaces?.selectedSpace?.id) || ALL_SPACES_OPTION;
  }, [spaces?.selectedSpace, spacesOptions]);

  return (
    <Box
      data-component="LibrarySearchBar"
      display="flex"
      gap={16}
      padding={padding}
      width="100%"
      alignItems="flex-end"
    >
      {filterTypes && filterTypes.length > 1 && (
        <Box flex={1} maxWidth={200}>
          {showLabels && (
            <InputLabel htmlFor="librarySearchBarFilterSelect" size="small">
              <FormattedMessage defaultMessage="Filters" id="zSOvI0" />
            </InputLabel>
          )}
          <SelectMenu
            fullWidth
            onChange={o => {
              if (o) {
                onFilterChange(o.value);
              }
            }}
            options={filterOptions}
            selected={selectedFilter ?? filterOptions[0]}
            variant={selectMenuVariant}
            size={selectMenuSize}
            id="librarySearchBarFilterSelect"
          />
        </Box>
      )}

      {spaces && (
        <Box flex={1} maxWidth={250}>
          {showLabels && (
            <InputLabel htmlFor="librarySearchBarSpaceSelect" size="small">
              <FormattedMessage defaultMessage="Spaces" id="ZMoczs" />
            </InputLabel>
          )}
          <SelectMenu
            filterable
            onChange={option => {
              if (option) spaces.onSelectedSpaceChange(option.value);
            }}
            selected={selectedSpaceOption}
            options={spacesOptions}
            variant={selectMenuVariant}
            size={selectMenuSize}
            menuHeight={385}
            fullWidth
            id="librarySearchBarSpaceSelect"
          />
        </Box>
      )}
      <Box flex={2}>
        <TextInput
          value={search}
          onChange={e => onSearchChange(e.target.value)}
          placeholder={intl.formatMessage({
            defaultMessage: 'Start typing to search...',
            id: '0MbifP',
          })}
          leftAdornment={
            <Box paddingLeft={8}>
              <Icon name="search" size={20} color="disabled" />
            </Box>
          }
          size={selectMenuSize}
        />
      </Box>
      {children}
    </Box>
  );
};

const LibrarySearchBar = LibrarySearchBarBase as typeof LibrarySearchBarBase & {
  CreateButton: typeof CreateButton;
};

LibrarySearchBar.CreateButton = CreateButton;

export { MenuListItem } from './CreateButton';
export { LibrarySearchBar };
